/**
 * WithdrawDialog — the requestor pulls their demand (or council does it on their
 * behalf). A reason is required. The backend decides the outcome: council, or a
 * requestor in new/scoping, closes it immediately; a requestor from triaging
 * onward files a pending withdrawal for council to confirm.
 */

import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { useWithdrawDemand } from '../hooks/useDemands'
import { apiErrorMessage } from '../lib/apiError'
import type { Demand } from '../types/demand'

const useStyles = makeStyles({
  surface: { maxWidth: '480px', width: '100%' },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  hint: { fontSize: '13px', color: tokens.colorNeutralForeground2 },
  error: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
  notes: { width: '100%' },
})

export interface WithdrawDialogProps {
  open: boolean
  demand: Demand
  onClose: () => void
  onWithdrawn?: (demand: Demand) => void
}

export function WithdrawDialog({ open, demand, onClose, onWithdrawn }: WithdrawDialogProps) {
  const styles = useStyles()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const mutation = useWithdrawDemand()
  const isPending = mutation.isPending

  useEffect(() => {
    if (open) {
      setReason('')
      setError(null)
    }
  }, [open, demand.id])

  const trimmed = reason.trim()
  const canConfirm = trimmed.length > 0 && !isPending

  function handleConfirm() {
    if (!canConfirm) return
    setError(null)
    mutation.mutate(
      { id: demand.id, reason: trimmed },
      {
        onSuccess: (d) => {
          onWithdrawn?.(d)
          onClose()
        },
        onError: (err: unknown) => {
          setError(apiErrorMessage(err, 'Failed to withdraw demand'))
        },
      },
    )
  }

  function handleOpenChange(_e: unknown, data: { open: boolean }) {
    if (!data.open && !isPending) onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="modal">
      <DialogSurface className={styles.surface} aria-label="Withdraw demand">
        <DialogBody>
          <DialogTitle>Withdraw demand</DialogTitle>
          <DialogContent className={styles.body}>
            <div className={styles.hint}>
              Withdrawing pulls this request. Once a council has started triaging it, your
              withdrawal is sent to them to confirm before it closes.
            </div>
            <Textarea
              className={styles.notes}
              placeholder="Reason for withdrawing…"
              value={reason}
              onChange={(_, d) => setReason(d.value)}
              resize="vertical"
              data-testid="withdraw-reason"
            />
            {error && <div className={styles.error}>{error}</div>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="withdraw-confirm"
            >
              {isPending ? 'Withdrawing…' : 'Withdraw'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
